import React, {useState, useEffect} from 'react';
import {useSelector} from 'react-redux';
import {RootState} from 'modules';
import {playbackRateList} from 'lib/playUtils';
import useKeyPress from 'hooks/useKeyPress';
import PlaybackRate from 'components/player/controlbar/PlaybackRate';
import {t} from 'lib/i18n';

/**
 * 설정 컴포넌트
 */

interface ISettingProps {
  displaySetting: {
    box: string;
    list: string;
  };
  videoDOM: HTMLVideoElement | null;
  settingList: string;
  setSettingList: React.Dispatch<React.SetStateAction<string>>;
  handleSettingClick: () => void;
  closeSetting: () => void;
}

const Setting = ({
  displaySetting,
  videoDOM,
  settingList,
  setSettingList,
  handleSettingClick,
  closeSetting,
}: ISettingProps) => {
  const {playingIdx} = useSelector((state: RootState) => ({
    playingIdx: state.file.playingIdx,
  }));

  const playbackRate = !videoDOM ? 1 : videoDOM.playbackRate;
  const [stateRate, setStateRate] = useState(playbackRate);
  const slowPress = useKeyPress('<'); // 재생속도 느리게
  const fastPress = useKeyPress('>'); // 재생속도 빠르게

  /**
   * 미디어 파일 바뀌면 이전 playRate로 셋팅
   */
  useEffect(() => {
    setPlaybackRate(stateRate);
  }, [playingIdx]);

  /**
   * 재생속도 느리게 키 이벤트 함수
   */
  useEffect(() => {
    if (slowPress) {
      const currentIdx = getPlaybackRateIdx();
      if (currentIdx < 0) return;

      let slowIdx = currentIdx - 1;
      if (slowIdx < 0) slowIdx = 0;

      const rate = playbackRateList[slowIdx];
      setPlaybackRate(rate);
    }
  }, [slowPress]);

  /**
   * 재생속도 빠르게 키 이벤트 함수
   */
  useEffect(() => {
    if (fastPress) {
      const currentIdx = getPlaybackRateIdx();
      if (currentIdx < 0) return;

      const listLen = playbackRateList.length;

      let fastIdx = currentIdx + 1;
      if (fastIdx >= listLen) fastIdx = listLen - 1;

      const rate = playbackRateList[fastIdx];
      setPlaybackRate(rate);
    }
  }, [fastPress]);

  /**
   * 재생속도 클릭 함수
   * @param e
   */
  const onClickPlaybackRate = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    const rate = Number(e.currentTarget.dataset.rate);

    setPlaybackRate(rate);
    closeSetting();
  };

  /**
   * 현재 video의 재생속도로 재생속도 리스트의 idx 가져옴
   */
  const getPlaybackRateIdx = () => {
    if (!videoDOM) return -1;

    const playbackRate = videoDOM.playbackRate; // 현재 재생속도
    const currentIdx = playbackRateList.indexOf(playbackRate);

    return currentIdx;
  };

  /**
   * 재생속도 셋팅
   * @param val 재생속도
   */
  const setPlaybackRate = (val: number) => {
    if (!videoDOM) return;

    videoDOM.playbackRate = val;
    setStateRate(val);
  };

  return (
    <div className={`setting_box ${displaySetting.box}`}>
      <button
        type='button'
        className='btn_setting'
        onClick={handleSettingClick}>
        <div className='tooltip'>
          <span>{t('설정')}</span>
          <em></em>
        </div>
      </button>
      <div
        className={`setting_list ${settingList}`}
        style={{display: displaySetting.list}}>
        <ul>
          <li>
            <button type='button' onClick={() => setSettingList('subLayer_on')}>
              <span>{t('재생속도')}</span>
              <em>{stateRate === 1 ? t('기본') : `${stateRate}x`}</em>
            </button>
          </li>
        </ul>
        <PlaybackRate
          setSettingList={setSettingList}
          stateRate={stateRate}
          onClickPlaybackRate={onClickPlaybackRate}
        />
      </div>
    </div>
  );
};

export default Setting;
